import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import tw from 'tailwind-styled-components';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import Config from '../Config';

const PaymentReturn = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    
    // Get link_id from URL query params
    const linkId = searchParams.get('link_id');
    
    const [status, setStatus] = useState('loading'); 
    const [message, setMessage] = useState('Verifying payment status...');

    useEffect(() => {
        const verifyPayment = async () => {
            if (!linkId) {
                setStatus('failed');
                setMessage('Invalid request. No payment link ID found.');
                return;
            }

            try {
               // Fetch payment link details
                const response = await axios.get(`${Config.apiUrl}/payment/link/${linkId}`, {
                    headers: { 
                        'authorization': user?.token,
                        'id': user?.userId || user?.id 
                    }
                });

                const linkData = response.data?.data || response.data;

                // check payment status
                if (linkData.link_status === 'PAID') {
                    setStatus('success');
                    setMessage('Payment successful! Your order has been confirmed.');
                } else if (linkData.link_status === 'EXPIRED') {
                    setStatus('failed');
                    setMessage('The payment link has expired.');
                } else {
                    setStatus('pending');
                    setMessage(`Payment status: ${linkData.link_status}`);
                }
            } catch (error) {
                console.error("Payment verification failed:", error);
                setStatus('failed');
                setMessage('Could not verify payment status. Please check "My Orders".');
            }
        };

        if (user?.token) {
            verifyPayment();
        } else {
            setStatus('failed');
            setMessage('Please log in to verify your payment status.');
        }
    }, [linkId, user]);

    return (
        <Container>
            <Card>
                {status === 'loading' && (
                    <>
                        <FaSpinner className="animate-spin text-4xl text-blue-500 mb-4" />
                        <Title>Verifying Payment</Title>
                        <Message>Please wait while we confirm your transaction...</Message>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <FaCheckCircle className="text-5xl text-green-500 mb-4" />
                        <Title>Payment Successful!</Title>
                        <Message>{message}</Message>
                        <ButtonGroup>
                            <PrimaryButton onClick={() => navigate('/orders')}>View My Orders</PrimaryButton>
                            <SecondaryButton onClick={() => navigate('/')}>Continue Shopping</SecondaryButton>
                        </ButtonGroup>
                    </>
                )}

                {(status === 'failed' || status === 'pending') && (
                    <>
                        {status === 'pending' ? (
                             <FaExclamationTriangle className="text-5xl text-yellow-600 mb-4" />
                        ) : (
                             <FaTimesCircle className="text-5xl text-red-500 mb-4" />
                        )}
                        <Title>{status === 'pending' ? 'Payment Pending' : 'Payment Failed'}</Title>
                        <Message>{message}</Message>
                        <ButtonGroup>
                            <PrimaryButton onClick={() => navigate('/orders')}>Go to My Orders</PrimaryButton>
                            <SecondaryButton onClick={() => navigate('/help')}>Contact Support</SecondaryButton>
                        </ButtonGroup>
                    </>
                )}
            </Card>
        </Container>
    );
};

// Styled Components
const Container = tw.div`min-h-screen bg-gray-50 flex items-center justify-center p-4`;
const Card = tw.div`bg-white rounded-xl shadow-lg p-8 max-w-md w-full flex flex-col items-center text-center`;
const Title = tw.h1`text-2xl font-bold text-gray-800 mb-2`;
const Message = tw.p`text-gray-600 mb-8`;
const ButtonGroup = tw.div`flex flex-col w-full gap-3`;
const PrimaryButton = tw.button`w-full py-3 bg-kuality-blue text-white rounded-lg font-semibold hover:bg-kuality-dark transition-colors`;
const SecondaryButton = tw.button`w-full py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors`;

export default PaymentReturn;